import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Menu, LayoutGrid, Smartphone } from 'lucide-react';
import api, { rehydrateToken } from './utils/api';
import wsService from './utils/websocket';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import NotificationBell from './components/NotificationBell';
import LoginAlertModal from './components/LoginAlertModal';
import Footer from './components/Footer';
import StorageCapBanner from './components/StorageCapBanner';

// Lazy-loaded route components
const EmployeeDashboard = lazy(() => import('./components/EmployeeDashboard'));
const ManagementDashboard = lazy(() => import('./components/ManagementDashboard'));
const Projects = lazy(() => import('./components/Projects'));
const ProjectWorkspace = lazy(() => import('./components/ProjectWorkspace'));
const CompetitorRERA = lazy(() => import('./components/CompetitorRERA'));
const MyTasks = lazy(() => import('./components/MyTasks'));
const TaskTemplates = lazy(() => import('./components/TaskTemplates'));
const TemplatesHub = lazy(() => import('./components/TemplatesHub'));
const DocumentsHub = lazy(() => import('./components/DocumentsHub'));
const RemindersHub = lazy(() => import('./components/RemindersHub'));
const Documents = lazy(() => import('./components/Documents'));
const Employees = lazy(() => import('./components/Employees'));
const Departments = lazy(() => import('./components/Departments'));
const EmployeeMatrix = lazy(() => import('./components/EmployeeMatrix'));
const Roles = lazy(() => import('./components/Roles'));
const Workflows = lazy(() => import('./components/Workflows'));
const ActivityLogs = lazy(() => import('./components/ActivityLogs'));
const WhatsAppSettings = lazy(() => import('./components/WhatsAppSettings'));
const ViewingMatrix = lazy(() => import('./components/ViewingMatrix'));
const Alerts = lazy(() => import('./components/Alerts'));
const AIInsights = lazy(() => import('./components/AIInsights'));
const Reports = lazy(() => import('./components/Reports'));
const DailyOperations = lazy(() => import('./components/DailyOperations'));
const Notifications = lazy(() => import('./components/Notifications'));
const DesktopActivity = lazy(() => import('./components/DesktopActivity'));
const Messaging = lazy(() => import('./components/Messaging'));
const SocietyMembers = lazy(() => import('./components/SocietyMembers'));
const CodeHealthDashboard = lazy(() => import('./components/CodeHealthDashboard'));
const ProjectTeam = lazy(() => import('./components/ProjectTeam'));
const ContractorInsurance = lazy(() => import('./components/ContractorInsurance'));
const ComplianceAlerts = lazy(() => import('./components/ComplianceAlerts'));
const AlertSettings = lazy(() => import('./components/AlertSettings'));
const DepartmentDocuments = lazy(() => import('./components/DepartmentDocuments'));
const MyDocuments = lazy(() => import('./components/MyDocuments'));
const ProjectSchedule = lazy(() => import('./components/ProjectSchedule'));
const LegalMilestonesOverview = lazy(() => import('./components/LegalMilestonesOverview'));
const DeletionRequestsAdmin = lazy(() => import('./components/DeletionRequestsAdmin'));
const FileHealth = lazy(() => import('./components/FileHealth'));
const ProjectDrawings = lazy(() => import('./components/ProjectDrawings'));
const AccessMatrix = lazy(() => import('./components/AccessMatrix'));
const AIChat = lazy(() => import('./components/AIChat'));
const TeamsWebhook = lazy(() => import('./components/TeamsWebhook'));
const Budget = lazy(() => import('./components/Budget'));
const LeasedAssets = lazy(() => import('./components/LeasedAssets'));
const CreditCardReminders = lazy(() => import('./components/CreditCardReminders'));
const ActivityReminders = lazy(() => import('./components/ActivityReminders'));
const DailyWorkReport = lazy(() => import('./components/DailyWorkReport'));
const HRResumeScanner = lazy(() => import('./components/HRResumeScanner'));
const LegalCases = lazy(() => import('./components/LegalCases'));
const CashFlow = lazy(() => import('./components/CashFlow'));
const LoginTransfer = lazy(() => import('./components/LoginTransfer'));
const BackupManagement = lazy(() => import('./components/BackupManagement'));
const DataQuality = lazy(() => import('./components/DataQuality'));
const AllMeetings = lazy(() => import('./components/AllMeetings'));
const SystemAdmin = lazy(() => import('./components/SystemAdmin'));
const StorageDashboard = lazy(() => import('./components/StorageDashboard'));
const EmailInboxSetup = lazy(() => import('./components/EmailInboxSetup'));
const UserGuide = lazy(() => import('./components/UserGuide'));
const PostSales = lazy(() => import('./components/PostSales'));
const PreSales = lazy(() => import('./components/PreSales'));
const PaymentDashboard = lazy(() => import('./components/PaymentDashboard'));
const TaskGantt = lazy(() => import('./components/TaskGantt'));
const Correspondence = lazy(() => import('./components/Correspondence'));
const DownloadManagement = lazy(() => import('./components/DownloadManagement'));
const Decisions = lazy(() => import('./components/Decisions'));
const OutdoorCampaigns = lazy(() => import('./components/OutdoorCampaigns'));
const NewPurchasers = lazy(() => import('./components/NewPurchasers'));
const BirthdayCalendar = lazy(() => import('./components/BirthdayCalendar'));
const LabourLicences = lazy(() => import('./components/LabourLicences'));
const SitePhotoAlbums = lazy(() => import('./components/SitePhotoAlbums'));
const Redevelopment = lazy(() => import('./components/Redevelopment'));
const Greetings = lazy(() => import('./components/Greetings'));
const RERACompliance = lazy(() => import('./components/RERACompliance'));
const GreenBuildingCompliance = lazy(() => import('./components/GreenBuildingCompliance'));
const Advertising = lazy(() => import('./components/Advertising'));
const Contracts = lazy(() => import('./components/Contracts'));
const WorkOrdersPage = lazy(() => import('./components/WorkOrdersPage'));
const NocSanctions = lazy(() => import('./components/NocSanctions'));
const FlatChanges = lazy(() => import('./components/FlatChanges'));
const ComplianceTracker = lazy(() => import('./components/ComplianceTracker'));
const NocRequirements = lazy(() => import('./components/NocRequirements'));
const PartnerPortal = lazy(() => import('./components/PartnerPortal'));
const InviteManagement = lazy(() => import('./components/InviteManagement'));
const EmployeeRegister = lazy(() => import('./components/EmployeeRegister'));
const FinancialDocuments = lazy(() => import('./components/FinancialDocuments'));
const CompanyManagement = lazy(() => import('./components/CompanyManagement'));
const Partners = lazy(() => import('./components/Partners'));
const SalesInventory = lazy(() => import('./components/SalesInventory'));
const RERAPaymentStages = lazy(() => import('./components/RERAPaymentStages'));
const MarketIntelligence = lazy(() => import('./components/MarketIntelligence'));
const LeaveManagement = lazy(() => import('./components/LeaveManagement'));
const CompanyCalendar = lazy(() => import('./components/CompanyCalendar'));
const VendorManagement = lazy(() => import('./components/VendorManagement'));
const Payroll = lazy(() => import('./components/Payroll'));
const CompetitorTracker = lazy(() => import('./components/CompetitorTracker'));
const OnlineUsers = lazy(() => import('./components/OnlineUsers'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const ChangePassword = lazy(() => import('./components/ChangePassword'));
const ProjectComparison = lazy(() => import('./components/ProjectComparison'));
const MSEDCLBills = lazy(() => import('./components/MSEDCLBills'));
const DocumentLibrary = lazy(() => import('./components/DocumentLibrary'));
const Reminders = lazy(() => import('./components/Reminders'));
const PropertyTax = lazy(() => import('./components/PropertyTax'));
const PublicAlbumViewer = lazy(() => import('./components/PublicAlbumViewer'));
const FlatChangePublicForm = lazy(() => import('./components/FlatChangePublicForm'));
const ConsultantPortal = lazy(() => import('./components/ConsultantPortal'));
const ConsultantManagement = lazy(() => import('./components/ConsultantManagement'));
const AttendanceCheckIn = lazy(() => import('./components/AttendanceCheckIn'));
const AttendanceAdmin = lazy(() => import('./components/AttendanceAdmin'));
const HRDashboard = lazy(() => import('./components/HRDashboard'));
const LeaveApprovalSettings = lazy(() => import('./components/LeaveApprovalSettings'));
const CustomerReviews = lazy(() => import('./components/CustomerReviews'));
const ReviewForm = lazy(() => import('./components/ReviewForm'));
const PublicTestimonials = lazy(() => import('./components/PublicTestimonials'));
const Approvals = lazy(() => import('./components/Approvals'));
const LeaveApprovals = lazy(() => import('./components/LeaveApprovals'));
const DrawingRequirementLibrary = lazy(() => import('./components/DrawingRequirementLibrary'));
const DrawingRequirements = lazy(() => import('./components/DrawingRequirements'));
const ProjectStatuses = lazy(() => import('./components/ProjectStatuses'));
const LeaveApprovalMatrix = lazy(() => import('./components/LeaveApprovalMatrix'));
const LegalResearch = lazy(() => import('./components/LegalResearch'));
const ExperienceLetters = lazy(() => import('./components/ExperienceLetters'));
const Masters = lazy(() => import('./components/Masters'));
const MyRemindersPage = lazy(() => import('./components/MyReminders'));
const PettyCash = lazy(() => import('./components/PettyCash'));
const SaleApprovals = lazy(() => import('./components/SaleApprovals'));
const PresentationBuilder = lazy(() => import('./components/PresentationBuilder'));
import '@/App.css';
import { toast } from 'sonner';

const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="text-slate-500 text-sm">Loading...</div>
  </div>
);

function getDefaultRoute(user) {
  const role = (user.role_name || user.role || '').toLowerCase().trim();
  // Partner routing must come BEFORE admin/manager to catch "Managing Partner"
  // and similar substrings.
  if (role === 'partner' || role.includes('partner')) return '/partner-portal';
  if (['admin', 'administrator', 'manager'].includes(role)) return '/dashboard/management';
  if (role === 'consultant') return '/consultant-portal';
  return '/dashboard/employee';
}

// Saves the intended path (e.g. /leave-approvals?token=...) before redirecting to /login
function UnauthRedirectCapture() {
  const { pathname, search } = window.location;
  try {
    // Only capture app routes we want to deep-link to after login
    const deepLinkable = ['/leave-approvals', '/sale-approvals'];
    if (deepLinkable.some(p => pathname.startsWith(p))) {
      sessionStorage.setItem('post_login_redirect', pathname + (search || ''));
    }
  } catch { /* ignore */ }
  return <Navigate to="/login" replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [accessProfile, setAccessProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLoginAlerts, setShowLoginAlerts] = useState(false);
  const [mobileCompact, setMobileCompact] = useState(() => {
    try { return localStorage.getItem('nirman_mobile_compact') === 'true'; } catch { return false; }
  });

  const toggleMobileCompact = () => {
    setMobileCompact(prev => {
      const next = !prev;
      try { localStorage.setItem('nirman_mobile_compact', String(next)); } catch { /* localStorage unavailable in private mode */ }
      return next;
    });
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Phase B (Feb 2026): the JWT is no longer persisted in sessionStorage.
    // On hard reload the in-memory store is empty, but the HttpOnly auth
    // cookie survives — `rehydrateToken()` uses the cookie to mint a fresh
    // in-memory access token before any component fires its first XHR.
    // If the cookie is missing/expired, this returns false and we drop
    // through to the login screen.
    const rehydrated = await rehydrateToken();
    if (rehydrated) {
      try {
        const userData = await api.getCurrentUser();
        setUser(userData);
        // Fetch access profile
        try {
          const profile = await api.getAccessProfile();
          setAccessProfile(profile);
        } catch (e) { toast.error("Something went wrong"); }
        // Connect WebSocket after successful auth
        wsService.connect();
      } catch (error) {
        api.clearToken();
      }
    }
    setLoading(false);
  };

  const handleLogin = async (userData) => {
    setUser(userData);
    // Fetch access profile after login
    try {
      const profile = await api.getAccessProfile();
      setAccessProfile(profile);
    } catch (e) { toast.error("Something went wrong"); }
    // Connect WebSocket on login
    wsService.connect();
    // Show login alerts modal
    setShowLoginAlerts(true);
  };

  const handleLogout = () => {
    // Disconnect WebSocket on logout
    wsService.disconnect();
    setUser(null);
    setAccessProfile(null);
    api.clearToken();
  };

  // In-place impersonation. Bypasses the full page reload that previously broke
  // on production when Cloudflare/WAF stripped the new `Set-Cookie` response
  // header (no cookie = `rehydrateToken()` returned 401 on reload = user thrown
  // back to the login page). We instead trust the in-memory token returned by
  // the impersonate endpoint, swap React state in place, and let the existing
  // auth flow do the rest. No reload required.
  const handleImpersonate = async (token, employee) => {
    api.clearToken();
    api.setToken(token);
    wsService.disconnect();
    setAccessProfile(null);
    setUser(employee);
    try {
      const profile = await api.getAccessProfile();
      setAccessProfile(profile);
    } catch { /* non-fatal; access profile fetches lazily later */ }
    wsService.connect();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" />
        {/* Top-of-page banner shown when the backend returns HTTP 507 with
            error_code=STORAGE_CAP_EXCEEDED from any upload endpoint.
            Mounted outside <Routes> so it persists across navigations. */}
        <StorageCapBanner />
        {!user ? (
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="/register" element={<EmployeeRegister />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/public/album/:token" element={<PublicAlbumViewer />} />
              <Route path="/a/:token" element={<PublicAlbumViewer />} />
              <Route path="/public/flat-change/:token" element={<FlatChangePublicForm />} />
              <Route path="/review/:token" element={<ReviewForm />} />
              <Route path="/testimonials" element={<PublicTestimonials />} />
              <Route path="*" element={<UnauthRedirectCapture />} />
            </Routes>
            <Footer />
          </Suspense>
        ) : (user.role_name || user.role || '').toLowerCase() === 'consultant' ? (
          /* Consultant gets a clean, simplified portal - no sidebar */
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="*" element={<ConsultantPortal user={user} onLogout={handleLogout} />} />
            </Routes>
          </Suspense>
        ) : (
          <div className={`flex app-with-sidebar ${mobileCompact ? 'mobile-compact' : ''}`}>
            {/* Mobile Header */}
            <div className="mobile-header" data-testid="mobile-header">
              <button
                onClick={() => setSidebarOpen(true)}
                className="hamburger-btn"
                data-testid="hamburger-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
              <span className="oswald text-white text-lg font-bold">NIRMAN WORKHUB</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={toggleMobileCompact}
                  className={`p-2 rounded transition-colors ${mobileCompact ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'}`}
                  data-testid="mobile-layout-toggle"
                  title={mobileCompact ? 'Switch to standard view' : 'Switch to compact mobile view'}
                >
                  {mobileCompact ? <Smartphone className="w-5 h-5" /> : <LayoutGrid className="w-5 h-5" />}
                </button>
                <NotificationBell user={user} />
              </div>
            </div>

            <Sidebar
              user={user}
              accessProfile={accessProfile}
              onLogout={handleLogout}
              onImpersonate={handleImpersonate}
              isOpen={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
            />
            <div className="main-content flex-1">
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route
                  path="/"
                  element={<Navigate to={getDefaultRoute(user)} replace />}
                />
                <Route path="/dashboard/employee" element={(user.role_name || user.role || '').toLowerCase() === 'partner' ? <Navigate to="/partner-portal" replace /> : <EmployeeDashboard user={user} />} />
                <Route path="/dashboard/management" element={(user.role_name || user.role || '').toLowerCase() === 'partner' ? <Navigate to="/partner-portal" replace /> : <ManagementDashboard user={user} mobileCompact={mobileCompact} />} />
                <Route path="/projects" element={<Projects user={user} />} />
                <Route path="/projects/:projectId" element={<ProjectWorkspace user={user} />} />
                <Route path="/competitor-rera" element={<CompetitorRERA user={user} />} />
                <Route path="/project-comparison" element={<ProjectComparison />} />
                <Route path="/msedcl" element={<MSEDCLBills />} />
                <Route path="/docs" element={<DocumentsHub user={user} />} />
                <Route path="/document-library" element={<DocumentsHub user={user} defaultTab="library" />} />
                <Route path="/documents" element={<DocumentsHub user={user} defaultTab="project" />} />
                <Route path="/reminders" element={<Reminders />} />
                <Route path="/tasks" element={<MyTasks user={user} showAll />} />
                <Route path="/my-tasks" element={<MyTasks user={user} />} />
                <Route path="/task-templates" element={<TemplatesHub user={user} />} />
                <Route path="/task-library" element={<TemplatesHub user={user} defaultTab="library" />} />
                <Route path="/templates" element={<TemplatesHub user={user} />} />
                <Route path="/employees" element={<Employees user={user} />} />
                <Route path="/departments" element={<Departments user={user} />} />
                <Route path="/employee-matrix" element={<EmployeeMatrix user={user} />} />
                <Route path="/roles" element={<Roles user={user} />} />
                <Route path="/workflows" element={<Workflows user={user} />} />
                <Route path="/activity" element={<ActivityLogs />} />
                <Route path="/alerts" element={<Alerts user={user} />} />
                <Route path="/ai-insights" element={<AIInsights user={user} />} />
                <Route path="/reports" element={<Reports user={user} />} />
                <Route path="/command-centre" element={<DailyOperations user={user} />} />
                <Route path="/notifications" element={<Notifications user={user} />} />
                <Route path="/desktop-activity" element={<DesktopActivity user={user} />} />
                <Route path="/messaging" element={<Messaging currentUser={user} />} />
                <Route path="/society-members" element={<SocietyMembers user={user} />} />
                <Route path="/code-health" element={<CodeHealthDashboard />} />
                <Route path="/project-team/:projectId" element={<ProjectTeam />} />
                <Route path="/contractor-insurance" element={<ContractorInsurance />} />
                <Route path="/activity-reminders" element={<RemindersHub user={user} defaultTab="activity" />} />
                <Route path="/compliance-alerts" element={<RemindersHub user={user} defaultTab="compliance" />} />
                <Route path="/alert-config" element={<Navigate to="/alert-settings?tab=routing" replace />} />
                <Route path="/alert-settings" element={<AlertSettings user={user} />} />
                <Route path="/department-docs" element={<DocumentsHub user={user} defaultTab="department" />} />
                <Route path="/my-documents" element={<DocumentsHub user={user} defaultTab="personal" />} />
                <Route path="/project-schedule" element={<ProjectSchedule />} />
                <Route path="/project-milestones" element={<LegalMilestonesOverview user={user} />} />
                <Route path="/deletion-requests" element={<DeletionRequestsAdmin user={user} />} />
                <Route path="/file-health" element={<FileHealth user={user} />} />
                <Route path="/project-drawings" element={<ProjectDrawings />} />
                <Route path="/access-matrix" element={<AccessMatrix />} />
                <Route path="/ai-chat" element={<AIChat user={user} />} />
                <Route path="/teams-webhook" element={<TeamsWebhook user={user} />} />
                <Route path="/budget" element={<Budget user={user} />} />
                <Route path="/leased-assets" element={<LeasedAssets user={user} />} />
                <Route path="/credit-cards" element={<RemindersHub user={user} defaultTab="credit-cards" />} />
                <Route path="/property-tax" element={<PropertyTax />} />
                <Route path="/daily-reports" element={<DailyWorkReport user={user} />} />
                <Route path="/hr" element={<HRResumeScanner user={user} />} />
                <Route path="/legal-cases" element={<LegalCases user={user} />} />
                <Route path="/cashflow" element={<CashFlow user={user} />} />
                <Route path="/login-transfer" element={<LoginTransfer user={user} />} />
                <Route path="/backup" element={<BackupManagement user={user} />} />
                <Route path="/admin/data-quality" element={<DataQuality />} />
                <Route path="/meetings" element={<AllMeetings />} />
                <Route path="/admin/system" element={<SystemAdmin user={user} />} />
                <Route path="/storage-dashboard" element={<StorageDashboard />} />
                <Route path="/admin/email-inbox" element={<EmailInboxSetup />} />
                <Route path="/user-guide" element={<UserGuide />} />
                <Route path="/post-sales" element={<PostSales />} />
                <Route path="/pre-sales" element={<PreSales />} />
                <Route path="/payment-dashboard" element={<PaymentDashboard />} />
                <Route path="/task-gantt" element={<TaskGantt />} />
                <Route path="/correspondence" element={<Correspondence user={user} />} />
                <Route path="/download-management" element={<DownloadManagement user={user} />} />
                <Route path="/decisions" element={<Decisions user={user} />} />
                <Route path="/outdoor-campaigns" element={<OutdoorCampaigns user={user} />} />
                <Route path="/new-purchasers" element={<NewPurchasers user={user} />} />
                <Route path="/birthday-calendar" element={<BirthdayCalendar user={user} />} />
                <Route path="/labour-licences" element={<LabourLicences user={user} />} />
                <Route path="/site-albums" element={<SitePhotoAlbums user={user} />} />
                <Route path="/presentations" element={<PresentationBuilder user={user} />} />
                <Route path="/public/album/:token" element={<PublicAlbumViewer />} />
                <Route path="/a/:token" element={<PublicAlbumViewer />} />
                <Route path="/public/flat-change/:token" element={<FlatChangePublicForm />} />
                <Route path="/redevelopment" element={<Redevelopment user={user} />} />
                <Route path="/greetings" element={<Greetings user={user} />} />
                <Route path="/rera-compliance" element={<RERACompliance user={user} />} />
                <Route path="/green-building" element={<GreenBuildingCompliance user={user} />} />
                <Route path="/noc-sanctions" element={<NocSanctions user={user} />} />
                <Route path="/flat-changes" element={<FlatChanges user={user} />} />
                <Route path="/compliance-tracker" element={<ComplianceTracker user={user} />} />
                <Route path="/noc-requirements" element={<NocRequirements />} />
                <Route path="/advertising" element={<Advertising user={user} />} />
                <Route path="/sales-inventory" element={<SalesInventory user={user} />} />
                <Route path="/rera-payment-stages" element={<RERAPaymentStages user={user} />} />
                <Route path="/market-intelligence" element={<MarketIntelligence user={user} />} />
                <Route path="/leave-management" element={<LeaveManagement user={user} />} />
                <Route path="/company-calendar" element={<CompanyCalendar user={user} />} />
                <Route path="/vendors" element={<VendorManagement user={user} />} />
                <Route path="/payroll" element={<Payroll user={user} />} />
                <Route path="/competitor-tracker" element={<CompetitorTracker user={user} />} />
                <Route path="/online-users" element={<OnlineUsers user={user} />} />
                <Route path="/contracts" element={<Contracts user={user} />} />
                <Route path="/work-orders" element={<WorkOrdersPage user={user} />} />
                <Route path="/attendance" element={<AttendanceAdmin user={user} />} />
                <Route path="/attendance/checkin" element={<AttendanceCheckIn user={user} />} />
                <Route path="/hr-dashboard" element={<HRDashboard user={user} />} />
                <Route path="/leave-approval-settings" element={<LeaveApprovalSettings user={user} />} />
                <Route path="/customer-reviews" element={<CustomerReviews user={user} />} />
                <Route path="/approvals" element={<Approvals user={user} />} />
                <Route path="/leave-approvals" element={<LeaveApprovals user={user} />} />
                <Route path="/drawing-library" element={<DrawingRequirementLibrary user={user} />} />
                <Route path="/drawing-requirements" element={<DrawingRequirements user={user} />} />
                <Route path="/project-statuses" element={<ProjectStatuses user={user} />} />
                <Route path="/leave-approval-matrix" element={<LeaveApprovalMatrix user={user} />} />
                <Route path="/legal-research" element={<LegalResearch user={user} />} />
                <Route path="/experience-letters" element={<ExperienceLetters user={user} letterType="experience" />} />
                <Route path="/appointment-letters" element={<ExperienceLetters user={user} letterType="appointment" />} />
                <Route path="/masters" element={<Masters user={user} />} />
                <Route path="/my-reminders" element={<RemindersHub user={user} defaultTab="mine" />} />
                <Route path="/petty-cash" element={<PettyCash user={user} />} />
                <Route path="/sale-approvals" element={<SaleApprovals user={user} />} />
                <Route path="/review/:token" element={<ReviewForm />} />
                <Route path="/testimonials" element={<PublicTestimonials />} />
                <Route path="/partner-portal" element={<PartnerPortal user={user} />} />
                <Route path="/consultants" element={<ConsultantManagement user={user} />} />
                <Route path="/invites" element={<InviteManagement user={user} />} />
                <Route path="/financial-documents" element={<FinancialDocuments user={user} />} />
                <Route path="/compare-documents" element={<Navigate to="/legal-research?tab=word-diff" replace />} />
                <Route path="/companies" element={<CompanyManagement user={user} />} />
                <Route path="/partners" element={<Partners user={user} />} />
                <Route path="/whatsapp" element={<WhatsAppSettings user={user} />} />
                <Route path="/viewing-matrix" element={<ViewingMatrix user={user} />} />
                <Route path="/change-password" element={<ChangePassword user={user} />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </Suspense>
            </div>
            <Footer />
            <LoginAlertModal show={showLoginAlerts} onClose={() => setShowLoginAlerts(false)} />
          </div>
        )}
      </BrowserRouter>
    </div>
  );
}

export default App;
